import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy, PLATFORM_ID, Inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ShowsApi, ApiTuneInProvider, ApiVOD, ApiPodcast, ApiProduction } from 'api';
import { Router } from '@angular/router';
import { Production, Show, LiveStream, VOD, Podcast, User } from 'models';
import { PlayerComponent } from 'media-playback/player/player.component';
import { LiveStreamsService } from 'common-ui/live-streams.service';
import { sleep, SubscriptionSet, AppConfig, Transfer, PageComponent, PageService, Redirection, Page, OpenGraph, Head } from 'common';
import { UserService } from 'user';
import { PlaybackSession, PlaylistType } from 'media-playback';
import { isPlatformServer } from '@angular/common';
import { ProductionsService } from '@tytapp/common-ui';
import { AirPlayService } from 'media-playback/airplay.service';

@Page('watch')
@Component({
  selector: 'tyt-watch-page',
  templateUrl: './watch-page.component.html',
  styleUrls: ['./watch-page.component.scss']
})
export class WatchPageComponent extends PageComponent implements AfterViewInit, OnDestroy {

  constructor(
    pageService : PageService,
    private route : ActivatedRoute,
    private productionsService : ProductionsService,
    private liveStreamsService : LiveStreamsService,
    private showsApi : ShowsApi,
    private userService : UserService,
    private appConfig : AppConfig,
    private redirection : Redirection,
    private opengraph : OpenGraph,
    private head : Head,
    
    @Inject(PLATFORM_ID)
    private platformId : any
  ) { 
    super(pageService);

    this.viewReady = new Promise((res, rej) => this.fireViewReady = res);
  }

  get pageComponentId() {
    return 'watch';
  }

  get title() {
    if (!this.currentItem)
      return null;

    // if (!this.currentItem.title.includes('__'))
    //   return this.currentItem.title;

    return this.currentItem.description;
  }

  @Transfer()
  production : Production;
  
  @Transfer()
  show : Show;
  
  @Transfer()
  activeLiveStreams : LiveStream[];
  
  @Transfer()
  activeLiveStream : LiveStream;
  
  @Transfer()
  mode : PlaylistType;
  
  @Transfer()
  currentItem : VOD;
  
  @Transfer()
  paywallVisible : boolean;
  
  @Transfer()
  user : User;

  @Transfer()
  playlist : any[];

  @Transfer()
  recentEpisodes : Production[] = [];
  
  @Transfer()
  showPlaybackOptions : boolean = false;

  @Transfer()
  startingVodID : any;

  subscriptions : SubscriptionSet = new SubscriptionSet();

  audienceType : string = 'visitor';

  @ViewChild('player', { static: true })
  player : PlayerComponent;

  get loginOrJoinUrl() {
    if (!this.user)
      return `/login`;
    return `/join`;
  }

  get entitled() {
    if (!this.user)
      return false;
    
    if (this.user.entitled)
      return true;

    if (this.user.membership && this.user.membership.entitled)
      return true;

    return false;
  }

  async playItem(vod, playlist): Promise<PlaybackSession> {
    this.currentItem = vod;
    await sleep(1);
    
    if (this.mode == 'podcasts')
      return await this.player.playPodcast(this.production, vod, this.mode, playlist);
    else
      return await this.player.playVOD(this.production, vod, this.mode, playlist);
    
  }

  getPodcastProviders(show : Show): ApiTuneInProvider[] {
    if (!show || !show.tune_ins)
      return [];
    
    let podcastTuneIn = show.tune_ins.find(x => x.type == 'podcast');

    if (!podcastTuneIn)
      return [];

    return podcastTuneIn.destinations;
  }
  
  get currentlyPlayingVOD(): VOD {
    return this.currentItem;
  }

  get currentlyPlayingPodcast(): Podcast {
    return this.currentItem;
  }

  get topicIdentifier() {
    if (!this.production || !this.currentItem)
      return null;

    return `vod_${this.currentItem.id}`;
  }

  get topicTitle() {
    if (!this.production || !this.currentItem)
      return null;

    return this.currentItem.description;
  }

  destroy() {
    this.subscriptions.unsubscribeAll();
  }

  notFound = true;

  async init() {
    this.subscriptions.subscribe(this.userService.userChanged, user => this.user = user);

    this.route.paramMap.subscribe(async params => {
      await sleep(1);

      let showID = params.get('show');
      let productionID = params.get('production');
      let mode : PlaylistType = <PlaylistType>params.get('mode');
      let vodID = params.get('vod');

      let verb = 'watch';

      if (mode === 'podcasts') 
        verb = 'listen';

      this.notFound = false;
      this.startingVodID = vodID;

      if (!this.hasServerState) {
        let production : ApiProduction;
        
        try {
          production = await this.productionsService.get(productionID);
        } catch (e) {
          if (e.statusCode === 404) {
            production = null;
          } else {
            this.redirection.go('/');
            throw e;
          }
        }

        if (!production) {
          this.redirection.go('/');
          return;
        }

        this.mode = mode;
        this.production = production;
        this.show = this.production.show as Show;
        this.activeLiveStreams = await this.liveStreamsService.active() as LiveStream[];
        this.activeLiveStream = this.activeLiveStreams.find(x => this.show && x.active_live_event && x.active_live_event.show && x.active_live_event.show.id == this.production.show.id);
        this.show = await this.showsApi.get(this.production.show.id).toPromise() as Show;
        
        if (showID)
          this.head.setCanonicalLink(`/${verb}/${this.show.slug || this.show.id}/${productionID}/${mode}/${vodID}`);
        else if (vodID)
          this.head.setCanonicalLink(`/${verb}/${productionID}/${mode}/${vodID}`);
        else
          this.head.setCanonicalLink(`/${verb}/${productionID}/${mode}`);

        if (this.production && this.production.show) {
          this.pageTitle = `${this.production.show.name} ${this.production.date_simple}`;
        } else {
          this.pageTitle = 'Watch';
        }

        await sleep(1);
        let playlist : (ApiVOD | ApiPodcast)[] = this.production.full_length_vods;

        if (this.mode == 'clips')
          playlist = this.production.vod_clips;
        else if (this.mode == 'podcasts')
          playlist = this.production.full_length_podcasts;
    
        if (!playlist) {
          console.error(`Failed to select playlist for mode '${this.mode}'`);
          playlist = [];
        }
        
        this.playlist = playlist;

        if (this.mode == 'episodes')
          this.recentEpisodes = await this.showsApi.getShowEpisodes(this.show.id, 0, 2).toPromise();
        else if (this.mode == 'clips')
          this.recentEpisodes = await this.showsApi.getShowClips(this.show.id, 0, 2).toPromise();
        else if (this.mode == 'podcasts')
          this.recentEpisodes = await this.showsApi.getShowPodcasts(this.show.id, 0, 2).toPromise();

        this.recentEpisodes = this.recentEpisodes.filter(x => x.id != this.production.id);


        let vod = this.production.vod_clips.find(x => x.id === vodID);
        if (!vod)
          vod = this.production.full_length_vods.find(x => x.id === vodID);

        if (vod) {
          let selfUrl = `https://tyt.com/watch/${this.production.show.id}/${this.production.id}/${mode}/${vod.id}`;

          this.opengraph.setMetadata({
            title: vod.title,
            description: vod.description,
            type: 'video.movie',
            url: selfUrl,
            siteName: 'TYT.com',
            image: vod.thumbnail_landscape1
          });

          let exposeVideoContentInLD = await this.appConfig.featureEnabled('apps.web.ld_expose_video');

          if (exposeVideoContentInLD) {
            try {
              this.opengraph.setLdJson({
                "@context": "http://schema.org",
                "@type": "VideoObject",
                url: selfUrl,
                name: vod.title || vod.description,
                description: vod.description,
                width: 1920,
                height: 1080,
                playerType: "HLS",
                videoQuality: "HD",
                thumbnailUrl: vod.thumbnail_landscape1 || production.thumbnail_landscape1 || this.show.thumbnail_landscape1,
                contentUrl: vod.asset && exposeVideoContentInLD ? vod.asset.url : undefined,
                duration: this.formatDurationPT(parseInt(vod.duration)),
                uploadDate: new Date(this.production.date).toISOString()
              });
            } catch (e) {
              console.error(`Failed to set LD+JSON for video:`);
              console.error(e);
            }
          }
        }
      }

      if (!isPlatformServer(this.platformId)) {
        await this.viewReady;

        let vod = this.playlist[0];
        if (this.startingVodID)
          vod = this.playlist.find(x => x.id == this.startingVodID);

        await this.playItem(vod, this.playlist);
        //this.airplayService.loadMedia(vod);
      }
    })

    this.audienceType = this.userService.audienceType;
  }

  private formatDurationPT(duration : number) {
    let hours = Math.floor(duration / (60*60));
    duration -= hours * 60*60;
    let minutes = Math.floor(duration / 60);
    duration -= minutes * 60;
    
    return `PT${this.zeroPad(hours)}H${this.zeroPad(minutes)}M${this.zeroPad(Math.floor(duration))}S`;
  }

  private zeroPad(number, digits = 2) {
    let str = `${number}`;
    while (str.length < digits)
      str = '0' + str;

    return str;
  }

  private viewReady : Promise<void>;
  private fireViewReady : Function;

  async ngAfterViewInit() {
    this.fireViewReady();
  }

}
